/**
 * WorkflowRecipesConfig -- the workflow configurator.
 *
 * Operator, 2026-08-06: "you still owe me a workflow configurator to manage
 * the logic waterfall of events that define when a contract is assembled,
 * signatures are gathered, etc."
 *
 * This is that surface. A recipe IS the waterfall: an ordered list of gates,
 * each with the actor who satisfies it and whether it is required, plus the
 * point at which the BINDING control instrument dispatches. Before the catalog
 * the ordering was implicit and split across three code paths, so no FI could
 * vary it and no session could read it end to end.
 *
 * Route:  /platform-config/workflow-recipes
 * Scope:  Aaim-operator (platform.aaim.com : 9303). Authoring and allowlist
 *         editing are platform-curation acts. The FI admin's act -- SELECTING
 *         from the allowlisted subset -- is a different persona on a different
 *         host (admin.aaim.com : 9300) and is not this page.
 *
 * WHAT THIS PAGE DOES NOT DO, deliberately:
 *
 *   It does not validate a draft. The backend's V1-V9 rules are the authority
 *   and return typed `violations`; this page renders them verbatim. A second
 *   copy of the rules in the SPA would be a parallel contract that drifts from
 *   the one that actually gates a publish -- the near-miss class
 *   .claude/rules/canon-fit-escalation.md exists to prevent. The consequence is
 *   deliberate: an invalid ordering is refused by the server, with its reason,
 *   rather than being greyed out client-side for a reason the UI invented.
 *
 *   It does not offer edit affordances on a PUBLISHED or RETIRED version. A
 *   published version is immutable (V5_VERSION_IMMUTABLE); rendering a Save
 *   button that the server would reject is the dead-write pattern
 *   .claude/rules/ui-package.md forbids.
 *
 * ONE deliberate exception to "let the server 422, never guess": the Publish
 * button is disabled on V9's two preconditions -- proofFlowId unset, and
 * proofFlowId naming a flow whose proof has never run green. Neither is a
 * re-implemented ordering judgment (V1-V8 stay server-only). Both are
 * unconditionally true for the DRAFT in hand, read from data this page already
 * holds, so firing the request would be a dead write with a guaranteed 422 and
 * no remedy the operator could act on from the response alone. Each disabled
 * state renders the reason and the remedy beside the button.
 *
 * The unproven half arrived with the operator ruling of 2026-08-30 (GH #2736):
 * flows.yaml `lastProvenGreen: null` means the proof EXISTS but has never run
 * green, and an ordering published on the strength of an unrun proof is a claim
 * the evidence does not support. Such a flow stays LISTABLE and storable on a
 * draft -- only publish is refused.
 *
 * The flow register offered in the selector is a HARDCODED mirror of
 * `.claude/manifests/flows.yaml` (id + lastProvenGreen), paired with
 * workflow-recipe.service.ts DECLARED_PROOF_FLOWS -- no endpoint serves it
 * today. All three copies are bound by
 * `services/platform-admin-api/src/services/__tests__/workflow-recipe.flows-parity.test.ts`,
 * which is what keeps the client-side check above from becoming a second
 * authority. Follow-on: expose an endpoint (e.g. GET
 * /api/v1/workflow-recipes/proof-flows) so this stops being a second copy of
 * that manifest.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Heading, BodyText, Card, Button, Input, PageHeader } from '@aaimplatform/ui';
import { Loader2, ArrowDown, Check, Minus, Zap } from 'lucide-react';
import {
  workflowRecipesApi,
  type RecipeActor,
  type RecipeGate,
  type RecipeStep,
  type RecipeStatus,
  type RecipeValidationError,
  type WorkflowRecipe,
} from '../../api/services/workflow-recipes';

/**
 * The label records are EXHAUSTIVE over the generated unions, so tsc fails if
 * the spec adds a gate or actor and this file is not updated -- and fails again
 * if a key is added here that the spec does not declare.
 *
 * The selector arrays are DERIVED from those records rather than hand-listed.
 * A hand-listed array typed `RecipeGate[]` is not exhaustive-checked: omitting a
 * gate compiles clean and silently drops it from the "add a gate" affordance,
 * which is the drift the Pledges status-filter test was written for after the
 * same shape shipped there. Key order is declaration order, which is the
 * canonical order the spec declares.
 */
const GATE_LABELS: Record<RecipeGate, string> = {
  PLEDGE_CREATED: 'Pledge created',
  CONTRACT_ASSEMBLED: 'Contract assembled',
  ADVISORY_NOTICE_SENT: 'Advisory notice sent',
  SIGNING_SESSION_OPENED: 'Signing session opened',
  SIGNATURE_CAPTURED: 'Signature captured',
  CONTRACT_FULLY_EXECUTED: 'Contract fully executed',
  RETURNED_TO_FI_REVIEW: 'Returned to FI review',
  FI_ACKNOWLEDGED: 'FI acknowledged',
  FI_ACCEPTED: 'FI accepted',
  CONTROL_INSTRUMENT_CONFIRMED: 'Control instrument confirmed',
};

const ACTOR_LABELS: Record<RecipeActor, string> = {
  MEMBER: 'Member',
  FI_STAFF: 'FI staff',
  ADVISOR: 'Advisor',
  PLATFORM: 'Platform',
  CUSTODIAN: 'Custodian',
};

/** Exported for the canonical-drift test; derived, never hand-listed. */
export const ALL_GATES = Object.keys(GATE_LABELS) as RecipeGate[];
export const ALL_ACTORS = Object.keys(ACTOR_LABELS) as RecipeActor[];

/**
 * V9's flow register (workflow-recipe.service.ts `DECLARED_PROOF_FLOWS` /
 * `validateProofFlow`), itself a hardcoded mirror of
 * `.claude/manifests/flows.yaml`. flows.yaml is read only by backend and repo
 * tooling; nothing serves this list over HTTP, so it is kept in sync by hand
 * until the follow-on endpoint noted in the module doc above exists.
 *
 * Each entry carries TWO facts, because V9 asks two questions:
 *
 *   `id`          -- flows.yaml declares this flow. It may be LISTED here and
 *                    STORED on a draft.
 *   `provenGreen` -- flows.yaml records a non-null `lastProvenGreen`; the
 *                    proof has actually run green. Only these can be PUBLISHED.
 *
 * A declared-but-unproven flow is deliberately still offered. Storing it on a
 * draft is how an operator records which proof an ordering is waiting on, and
 * the server accepts that write; only PUBLISH is refused. Hiding the option
 * would leave the operator with no way to express the pending state and no
 * indication that the flow exists at all.
 *
 * The service holds the same pair and `workflow-recipe.flows-parity.test.ts`
 * asserts all three copies -- manifest, service, this file -- agree on BOTH
 * fields in both directions. That binding is what makes the client-side
 * `canPublish` check below safe rather than a second authority.
 */
// adhoc-type-skip: projection of .claude/manifests/flows.yaml (a repo manifest, not a wire contract) -- the flows-parity test binds this shape to the manifest field-for-field; declaring it in an OpenAPI spec would mint an HTTP schema for a value that never crosses the wire, inverting the arbiter (flows.yaml owns it).
export interface DeclaredProofFlow {
  readonly id: string;
  readonly provenGreen: boolean;
}

export const DECLARED_PROOF_FLOWS: readonly DeclaredProofFlow[] = [
  { id: 'fi-tenant-provisioning', provenGreen: true },
  { id: 'fi-identity-provider-binding', provenGreen: true },
  { id: 'fi-staff-step-up-pledge-inbox', provenGreen: true },
  { id: 'member-pledge-to-fi-decision', provenGreen: true },
  { id: 'ndfcu-fi-context-prod', provenGreen: true },
  { id: 'ndfcu-member-connected-prod', provenGreen: true },
  { id: 'ndfcu-capacity-band-prod', provenGreen: true },
  { id: 'member-pledge-to-awaiting-signature', provenGreen: true },
  { id: 'ndfcu-member-contract-signing-prod', provenGreen: true },
  { id: 'ndfcu-inbox-decision-prod', provenGreen: true },
  { id: 'ndfcu-issuance-monitoring-prod', provenGreen: true },
  { id: 'tempo-demo-fi-admin-walkthrough-prod', provenGreen: true },
];

/** Derived, never hand-listed -- a second literal is drift the parity test could not see. */
export const DECLARED_PROOF_FLOW_IDS = DECLARED_PROOF_FLOWS.map((f) => f.id);

/**
 * Mirrors workflow-recipe.service.ts `isProofFlowProvenGreen`, including its
 * optional `register` (default: the mirror above).
 *
 * The parameter exists because every flow in the real manifest has run green
 * since 2026-09-15 (ef0726dfb0), so the real register can no longer exercise
 * the unproven branch. The service took the same seam in that commit; this page
 * did not, and 5 of its tests across both suites then failed against the
 * all-proven mirror. A fixture register is how the branch stays tested without
 * writing a false `provenGreen: false` into a copy the parity test binds to
 * flows.yaml.
 */
export function isProofFlowProvenGreen(
  proofFlowId: string,
  register: readonly DeclaredProofFlow[] = DECLARED_PROOF_FLOWS,
): boolean {
  return register.some((f) => f.id === proofFlowId && f.provenGreen);
}

/**
 * The option text. An unproven flow reads as unproven in the selector itself --
 * the operator learns it cannot back a publish at the moment of choosing, not
 * after saving and finding Publish greyed out.
 */
export function proofFlowOptionLabel(flow: DeclaredProofFlow): string {
  return flow.provenGreen ? flow.id : `${flow.id} -- unproven, cannot publish`;
}

/** Matches the RecipeIdParam / recipeId schema pattern in the canonical spec. */
const RECIPE_ID_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** The platform default family, used to seed a brand-new recipe. */
const PLATFORM_DEFAULT_RECIPE_ID = 'aaim-default';

/**
 * The ordering a new recipe family falls back to when the catalog is empty.
 * Two properties matter, and a validator can only check the first.
 *
 * VALID: it carries SIGNATURE_CAPTURED and the bindingDispatchAfter gate (V8),
 * with the trigger strictly after the signature (V1).
 *
 * RUNNABLE: the trigger is FI_ACCEPTED, which today is the only gate that
 * dispatches anything. Custodian issuance fires from exactly one place --
 * platform-admin-api `src/routes/pledge.routes.ts`, gated on
 * `resolved.effectiveBindingGate === 'FI_ACCEPTED'` -- and the signature-side
 * seam in `pledge-signature-reconciler.worker.ts` is a deliberate stub that
 * logs `recipe.dispatch.deferred_no_credential` rather than dispatching. So an
 * ordering whose EFFECTIVE trigger is any other gate either fails loudly at
 * that stub or, for a gate with no producer at all, never fires and never logs.
 *
 * This template read [SIGNATURE_CAPTURED, CONTROL_INSTRUMENT_CONFIRMED] with
 * the trigger on CONTROL_INSTRUMENT_CONFIRMED until 2026-08-18, chosen as the
 * minimal ordering the server would ACCEPT. It passed V1/V4/V8 and could never
 * dispatch: nothing on this platform produces CONTROL_INSTRUMENT_CONFIRMED. So
 * every draft created through this page began from an ordering that cannot run,
 * and no gate could say so -- validity and runnability are different questions.
 */
export const FALLBACK_NEW_VERSION_STEPS: RecipeStep[] = [
  { gate: 'SIGNATURE_CAPTURED', actor: 'MEMBER', required: true },
  { gate: 'FI_ACCEPTED', actor: 'FI_STAFF', required: true },
];
export const FALLBACK_NEW_VERSION_DISPATCH: RecipeGate = 'FI_ACCEPTED';

/** RecipeStatus in words; exhaustive over the generated union like the maps above. */
export const RECIPE_STATUS_LABELS: Record<RecipeStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  RETIRED: 'Retired',
};

function statusVariant(status: RecipeStatus): 'success' | 'warning' | 'neutral' {
  if (status === 'PUBLISHED') return 'success';
  if (status === 'DRAFT') return 'warning';
  return 'neutral';
}

function RecipeStatusBadge({ status }: { status: RecipeStatus }) {
  return <Badge variant={statusVariant(status)}>{RECIPE_STATUS_LABELS[status]}</Badge>;
}

function ViolationList({ violations, message }: { violations: RecipeValidationError[]; message: string }) {
  return (
    <Alert type="error" title="The server refused this change">
      <BodyText variant="ui" size="sm">{message}</BodyText>
      {violations.length > 0 && (
        <ul className="mt-2 space-y-1">
          {violations.map((v, i) => (
            <li key={`${v.code}-${i}`} className="text-sm">
              <span className="font-mono text-xs mr-2">{v.code}</span>
              {v.message}
            </li>
          ))}
        </ul>
      )}
    </Alert>
  );
}

export interface WorkflowRecipesConfigPageProps {
  /**
   * The proof-flow register the selector offers and the Publish precondition
   * reads. Omitted in production (App.tsx routes the page with no props), so the
   * page reads DECLARED_PROOF_FLOWS. A test passes a fixture register carrying an
   * unproven flow, the same seam `isProofFlowProvenGreen` takes.
   */
  readonly proofFlowRegister?: readonly DeclaredProofFlow[];
}

export function WorkflowRecipesConfigPage({
  proofFlowRegister = DECLARED_PROOF_FLOWS,
}: WorkflowRecipesConfigPageProps = {}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<{ recipeId: string; version: number } | null>(null);
  const [draft, setDraft] = useState<RecipeStep[] | null>(null);
  const [draftDispatch, setDraftDispatch] = useState<RecipeGate | null>(null);
  // undefined = untouched (fall back to the stored value); string | null = touched.
  // A tri-state distinct from draftDispatch's binary one because null IS a valid
  // stored value here (an unproven DRAFT legitimately has no proofFlowId), so a
  // plain `?? current` could not tell "operator cleared it" from "never touched".
  const [draftProofFlowId, setDraftProofFlowId] = useState<string | null | undefined>(undefined);
  const [failure, setFailure] = useState<{ violations: RecipeValidationError[]; message: string } | null>(null);
  const [newRecipeId, setNewRecipeId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newFailure, setNewFailure] = useState<{ violations: RecipeValidationError[]; message: string } | null>(null);

  const catalog = useQuery({
    queryKey: ['workflow-recipes'],
    queryFn: () => workflowRecipesApi.list(),
  });

  const version = useQuery({
    queryKey: ['workflow-recipe-version', selected?.recipeId, selected?.version],
    queryFn: () =>
      selected ? workflowRecipesApi.getVersion(selected.recipeId, selected.version) : null,
    enabled: !!selected,
  });

  const current: WorkflowRecipe | null = version.data ?? null;
  const isDraft = current?.status === 'DRAFT';

  // The steps under edit: the local draft once the operator has touched
  // anything, otherwise the server's copy. Never a merge of the two -- a
  // half-local half-server ordering is a shape neither side ever agreed to.
  const steps: RecipeStep[] = draft ?? current?.steps ?? [];
  const dispatchAfter: RecipeGate | null = draftDispatch ?? current?.bindingDispatchAfter ?? null;
  const proofFlowId: string | null =
    draftProofFlowId !== undefined ? draftProofFlowId : current?.proofFlowId ?? null;
  const dirty = draft !== null || draftDispatch !== null || draftProofFlowId !== undefined;
  // V9's TWO preconditions, checked client-side ONLY to keep the Publish button
  // honest (see the module doc) -- never as a stand-in for the ordering rules.
  // Both are unconditionally true for the draft in hand, so firing the request
  // would be a dead write with a guaranteed 422 and no remedy in the response.
  // Both read from the mirror the parity test binds to flows.yaml, so neither
  // can quietly disagree with the validator that actually gates the publish.
  // `proofFlowProven` subsumes the set check (an unset flow is not a proven
  // one), so canPublish reads from it alone. The two states are still reported
  // SEPARATELY beside the button, because the remedies differ: pick a flow,
  // versus run that flow's proof.
  const proofFlowProven =
    proofFlowId !== null && isProofFlowProvenGreen(proofFlowId, proofFlowRegister);
  const canPublish = proofFlowProven;

  const usedGates = useMemo(() => new Set(steps.map((s) => s.gate)), [steps]);
  const addableGates = ALL_GATES.filter((g) => !usedGates.has(g));

  function selectVersion(recipeId: string, v: number) {
    setSelected({ recipeId, version: v });
    setDraft(null);
    setDraftDispatch(null);
    setDraftProofFlowId(undefined);
    setFailure(null);
  }

  function mutateSteps(next: RecipeStep[]) {
    setDraft(next);
    setFailure(null);
  }

  const saveDraft = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('no version selected');
      return workflowRecipesApi.updateDraft(selected.recipeId, selected.version, {
        ...(draft ? { steps: draft } : {}),
        ...(draftDispatch ? { bindingDispatchAfter: draftDispatch } : {}),
        ...(draftProofFlowId !== undefined ? { proofFlowId: draftProofFlowId } : {}),
      });
    },
    onSuccess: (result) => {
      if (result.ok) {
        setDraft(null);
        setDraftDispatch(null);
        setDraftProofFlowId(undefined);
        setFailure(null);
        void queryClient.invalidateQueries({ queryKey: ['workflow-recipe-version'] });
        void queryClient.invalidateQueries({ queryKey: ['workflow-recipes'] });
      } else {
        setFailure({ violations: result.violations, message: result.message });
      }
    },
  });

  const publish = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('no version selected');
      return workflowRecipesApi.publish(selected.recipeId, selected.version);
    },
    onSuccess: (result) => {
      if (result.ok) {
        setFailure(null);
        void queryClient.invalidateQueries({ queryKey: ['workflow-recipe-version'] });
        void queryClient.invalidateQueries({ queryKey: ['workflow-recipes'] });
      } else {
        setFailure({ violations: result.violations, message: result.message });
      }
    },
  });

  const retire = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('no version selected');
      return workflowRecipesApi.retire(selected.recipeId, selected.version);
    },
    onSuccess: (result) => {
      if (result.ok) {
        setFailure(null);
        void queryClient.invalidateQueries({ queryKey: ['workflow-recipe-version'] });
        void queryClient.invalidateQueries({ queryKey: ['workflow-recipes'] });
      } else {
        setFailure({ violations: result.violations, message: result.message });
      }
    },
  });

  // What a newly minted draft starts from, in order of preference: the
  // family's OWN highest version, then the platform default, then the fallback
  // above. Seeding a second version of `acknowledge-gated` from a generic
  // skeleton would silently discard that family's ordering -- a new version of
  // a recipe starts from the current one. The catalog carries whole
  // WorkflowRecipe objects (steps and bindingDispatchAfter included), so this
  // reads data already in hand rather than issuing a second request.
  function seedForNewVersion(recipeId: string): {
    steps: RecipeStep[];
    bindingDispatchAfter: RecipeGate;
  } {
    const recipes = catalog.data?.recipes ?? [];
    const family = recipes
      .filter((r) => r.recipeId === recipeId)
      .sort((a, b) => b.version - a.version)[0];
    const seed = family ?? recipes.find((r) => r.recipeId === PLATFORM_DEFAULT_RECIPE_ID);
    if (!seed) {
      return {
        steps: FALLBACK_NEW_VERSION_STEPS,
        bindingDispatchAfter: FALLBACK_NEW_VERSION_DISPATCH,
      };
    }
    return { steps: seed.steps, bindingDispatchAfter: seed.bindingDispatchAfter };
  }

  // Mints a DRAFT at version 1 for a recipeId the catalog has never seen (a
  // new family), or the next version for one it has -- createWorkflowRecipeVersion
  // decides which from the id alone. Seeded per seedForNewVersion above so the
  // created draft is both editable in the panel to the right AND runnable.
  const newRecipeIdTrimmed = newRecipeId.trim();
  const newTitleTrimmed = newTitle.trim();
  const canCreateVersion =
    RECIPE_ID_PATTERN.test(newRecipeIdTrimmed) && newTitleTrimmed.length > 0;

  const createVersion = useMutation({
    mutationFn: async () => {
      const seed = seedForNewVersion(newRecipeIdTrimmed);
      return workflowRecipesApi.createVersion(newRecipeIdTrimmed, {
        title: newTitleTrimmed,
        steps: seed.steps,
        bindingDispatchAfter: seed.bindingDispatchAfter,
        proofFlowId: null,
      });
    },
    onSuccess: (result) => {
      if (result.ok) {
        setNewFailure(null);
        setNewRecipeId('');
        setNewTitle('');
        void queryClient.invalidateQueries({ queryKey: ['workflow-recipes'] });
        selectVersion(result.data.recipeId, result.data.version);
      } else {
        setNewFailure({ violations: result.violations, message: result.message });
      }
    },
  });

  return (
    <div>
      <PageHeader
        description="The ordering a pledge runs through: which events must occur, in what order, who satisfies each, and when the control instrument dispatches. A published version is immutable; vary an ordering by publishing a new version, then allowlisting it for the tenants that may select it."
      />
      <div className="space-y-6">

      {catalog.isError && (
        <Alert type="error" title="Catalog unavailable">
          The recipe catalog could not be loaded. Recipes are served by
          platform-admin-api; check its logs before assuming the catalog is empty.
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ---- Catalog ------------------------------------------------- */}
        <Card className="p-6 lg:col-span-1">
          <Heading level={2} as={3} size="card">Catalog</Heading>
          {catalog.isLoading ? (
            <div className="flex items-center gap-2 mt-4 text-secondary">
              <Loader2 className="w-4 h-4 animate-spin" />
              <BodyText variant="ui" size="sm">Loading recipes...</BodyText>
            </div>
          ) : (catalog.data?.recipes.length ?? 0) === 0 ? (
            <BodyText variant="ui" size="sm" color="secondary" className="mt-4">
              No recipes exist yet.
            </BodyText>
          ) : (
            <ul className="mt-4 space-y-3">
              {catalog.data?.recipes.map((r) => (
                <li key={`${r.recipeId}-${r.version}`}>
                  <button
                    onClick={() => selectVersion(r.recipeId, r.version)}
                    className={`w-full text-left px-3 py-2 rounded-sm border transition-colors ${
                      selected?.recipeId === r.recipeId && selected?.version === r.version
                        ? 'border-accent bg-accent/5'
                        : 'border-recessed hover:bg-elevated'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm">{r.title}</span>
                      <RecipeStatusBadge status={r.status} />
                    </div>
                    <div className="font-mono text-xs text-tertiary mt-1">
                      {r.recipeId} v{r.version}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-5 pt-4 border-t border-recessed space-y-2">
            <Heading level={3} as={4} size="label">New draft version</Heading>
            <BodyText variant="ui" size="sm" color="secondary">
              An existing recipe id mints its next version, seeded from that
              recipe&apos;s current ordering; a not-yet-seen id starts a new
              family, seeded from the platform default. Refine it in the panel
              once created.
            </BodyText>
            {selected && (
              <button
                type="button"
                onClick={() => {
                  setNewRecipeId(selected.recipeId);
                  setNewTitle(current?.title ?? '');
                  setNewFailure(null);
                }}
                className="text-xs text-accent hover:underline"
              >
                Prefill from {selected.recipeId}
              </button>
            )}
            <Input
              id="new-recipe-id"
              label="Recipe id"
              helperText="kebab-case, for example acknowledge-gated"
              value={newRecipeId}
              onChange={(e) => {
                setNewRecipeId(e.target.value);
                setNewFailure(null);
              }}
              className="font-mono"
            />
            <Input
              id="new-recipe-title"
              label="Title"
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
                setNewFailure(null);
              }}
            />
            <Button
              variant="secondary"
              disabled={!canCreateVersion || createVersion.isPending}
              onClick={() => createVersion.mutate()}
            >
              {createVersion.isPending ? 'Creating...' : 'Create draft'}
            </Button>
            {newFailure && (
              <ViolationList violations={newFailure.violations} message={newFailure.message} />
            )}
          </div>
        </Card>

        {/* ---- The waterfall ------------------------------------------- */}
        <Card className="p-6 lg:col-span-2">
          {!selected ? (
            <BodyText variant="ui" size="sm" color="secondary">
              Select a recipe version to view its ordering.
            </BodyText>
          ) : version.isLoading ? (
            <div className="flex items-center gap-2 text-secondary">
              <Loader2 className="w-4 h-4 animate-spin" />
              <BodyText variant="ui" size="sm">Loading ordering...</BodyText>
            </div>
          ) : !current ? (
            <BodyText variant="ui" size="sm" color="secondary">This version no longer exists.</BodyText>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Heading level={2} as={3} size="card">{current.title}</Heading>
                  <div className="font-mono text-xs text-tertiary mt-1">
                    {current.recipeId} v{current.version}
                  </div>
                </div>
                <RecipeStatusBadge status={current.status} />
              </div>

              {!isDraft && (
                <div className="p-4 rounded-sm bg-accent/5 border border-recessed">
                  <BodyText variant="ui" size="sm" className="text-secondary">
                    {current.status === 'PUBLISHED'
                      ? 'A published version is immutable. To change this ordering, publish a new version.'
                      : 'This version is retired and cannot be edited or selected.'}
                  </BodyText>
                </div>
              )}

              {failure && <ViolationList violations={failure.violations} message={failure.message} />}

              {/* The ordering itself. */}
              <ol className="space-y-2">
                {steps.map((step, i) => (
                  <li key={step.gate}>
                    <div className="flex items-center gap-3 p-3 rounded-sm border border-recessed">
                      <span className="font-mono text-xs text-tertiary w-6">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{GATE_LABELS[step.gate]}</div>
                        <div className="font-mono text-xs text-tertiary">{step.gate}</div>
                      </div>

                      {isDraft ? (
                        <>
                          <select
                            aria-label={`Actor for ${GATE_LABELS[step.gate]}`}
                            value={step.actor}
                            onChange={(e) =>
                              mutateSteps(
                                steps.map((s, j) =>
                                  j === i ? { ...s, actor: e.target.value as RecipeActor } : s,
                                ),
                              )
                            }
                            className="text-xs border border-recessed rounded-sm px-2 py-1 bg-elevated"
                          >
                            {ALL_ACTORS.map((a) => (
                              <option key={a} value={a}>{ACTOR_LABELS[a]}</option>
                            ))}
                          </select>
                          <label className="flex items-center gap-1 text-xs text-secondary">
                            <input
                              type="checkbox"
                              checked={step.required}
                              onChange={(e) =>
                                mutateSteps(
                                  steps.map((s, j) =>
                                    j === i ? { ...s, required: e.target.checked } : s,
                                  ),
                                )
                              }
                            />
                            Required
                          </label>
                          <div className="flex gap-1">
                            <button
                              aria-label={`Move ${GATE_LABELS[step.gate]} earlier`}
                              disabled={i === 0}
                              onClick={() => {
                                const next = [...steps];
                                [next[i - 1], next[i]] = [next[i], next[i - 1]];
                                mutateSteps(next);
                              }}
                              className="px-2 py-1 text-xs border border-recessed rounded-sm disabled:opacity-40"
                            >
                              Up
                            </button>
                            <button
                              aria-label={`Move ${GATE_LABELS[step.gate]} later`}
                              disabled={i === steps.length - 1}
                              onClick={() => {
                                const next = [...steps];
                                [next[i], next[i + 1]] = [next[i + 1], next[i]];
                                mutateSteps(next);
                              }}
                              className="px-2 py-1 text-xs border border-recessed rounded-sm disabled:opacity-40"
                            >
                              Down
                            </button>
                            <button
                              aria-label={`Remove ${GATE_LABELS[step.gate]}`}
                              onClick={() => mutateSteps(steps.filter((_, j) => j !== i))}
                              className="px-2 py-1 text-xs border border-recessed rounded-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="text-xs text-secondary">{ACTOR_LABELS[step.actor]}</span>
                          <span className="flex items-center gap-1 text-xs text-tertiary">
                            {step.required ? <Check className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                            {step.required ? 'Required' : 'Optional'}
                          </span>
                        </>
                      )}
                    </div>

                    {dispatchAfter === step.gate && (
                      <div className="flex items-center gap-2 pl-9 py-1 text-xs text-accent">
                        <Zap className="w-3 h-3" />
                        Control instrument (BINDING) dispatches here
                      </div>
                    )}
                    {i < steps.length - 1 && (
                      <div className="pl-9 py-0.5 text-tertiary">
                        <ArrowDown className="w-3 h-3" />
                      </div>
                    )}
                  </li>
                ))}
              </ol>

              {isDraft && addableGates.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-secondary" htmlFor="add-gate">Add a gate</label>
                  <select
                    id="add-gate"
                    value=""
                    onChange={(e) => {
                      const g = e.target.value as RecipeGate;
                      if (!g) return;
                      mutateSteps([...steps, { gate: g, actor: 'PLATFORM', required: true }]);
                    }}
                    className="text-xs border border-recessed rounded-sm px-2 py-1 bg-elevated"
                  >
                    <option value="">Select...</option>
                    {addableGates.map((g) => (
                      <option key={g} value={g}>{GATE_LABELS[g]}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center gap-2">
                <label className="text-xs text-secondary" htmlFor="dispatch-after">
                  BINDING dispatches after
                </label>
                {isDraft ? (
                  <select
                    id="dispatch-after"
                    value={dispatchAfter ?? ''}
                    onChange={(e) => {
                      setDraftDispatch(e.target.value as RecipeGate);
                      setFailure(null);
                    }}
                    className="text-xs border border-recessed rounded-sm px-2 py-1 bg-elevated"
                  >
                    {steps.map((s) => (
                      <option key={s.gate} value={s.gate}>{GATE_LABELS[s.gate]}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-xs text-secondary">
                    {dispatchAfter ? GATE_LABELS[dispatchAfter] : 'Not set'}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <label className="text-xs text-secondary" htmlFor="proof-flow">
                  Proof flow (required to publish)
                </label>
                {isDraft ? (
                  <select
                    id="proof-flow"
                    value={proofFlowId ?? ''}
                    onChange={(e) => {
                      setDraftProofFlowId(e.target.value === '' ? null : e.target.value);
                      setFailure(null);
                    }}
                    className="text-xs border border-recessed rounded-sm px-2 py-1 bg-elevated font-mono"
                  >
                    <option value="">None -- stays DRAFT</option>
                    {proofFlowRegister.map((flow) => (
                      <option key={flow.id} value={flow.id}>
                        {proofFlowOptionLabel(flow)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-xs font-mono text-secondary">
                    {current.proofFlowId ?? 'none'}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3 pt-2 border-t border-recessed">
                {isDraft && (
                  <>
                    <Button
                      variant="primary"
                      disabled={!dirty || saveDraft.isPending}
                      onClick={() => saveDraft.mutate()}
                    >
                      {saveDraft.isPending ? 'Saving...' : 'Save draft'}
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={dirty || !canPublish || publish.isPending}
                      onClick={() => publish.mutate()}
                    >
                      {publish.isPending ? 'Publishing...' : 'Publish'}
                    </Button>
                    {dirty ? (
                      <BodyText variant="ui" size="sm" color="secondary">
                        Save before publishing -- publish acts on the stored version, not on
                        unsaved edits.
                      </BodyText>
                    ) : proofFlowId === null ? (
                      <BodyText variant="ui" size="sm" color="secondary">
                        Publish needs a proof flow. Set &quot;Proof flow&quot; above to a
                        flows.yaml id, then save the draft -- V9 rejects a publish with no
                        proofFlowId.
                      </BodyText>
                    ) : !proofFlowProven ? (
                      <BodyText variant="ui" size="sm" color="secondary">
                        Publish needs a PROVEN proof flow. {proofFlowId} is declared in
                        flows.yaml but its proof has never run green (lastProvenGreen is
                        null), so V9 rejects the publish. Run that proof and record
                        lastProvenGreen, or select a proven flow. The draft keeps this
                        selection either way.
                      </BodyText>
                    ) : null}
                  </>
                )}
                {current.status === 'PUBLISHED' && (
                  <Button
                    variant="secondary"
                    disabled={retire.isPending}
                    onClick={() => retire.mutate()}
                  >
                    {retire.isPending ? 'Retiring...' : 'Retire'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
      </div>
    </div>
  );
}
